import React, { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';

import companyname from "../media/LOGO.png";
import kiwi from "../media/KIWI.jpg";
import market from "../media/Market.jpg";
import raspberry from "../media/Raspberry.jpg";
import trading from "../media/Trading.jpg";
import swayambhunath from "../media/Swaymbhunath.mp4";
import office1 from '../media/office1.png';
import office2 from '../media/office2.png';
import agrofarm from "../media/Brahma Jyoti Multi Agro Farm.png";
import greenenergy from "../media/Brahma Jyoti Green Energy.png";
import financial from "../media/Brahma Jyoti Investment & Equity Management.png";
import malePlaceholder from "../media/ManagementTeam/male.png";

import { Layout, Typography, Row, Col, Card } from 'antd';
import axios from 'axios';
import config from "../config.json";
import { managementTeam } from "../Data/siteData";

const homeImages = [
  companyname,
  kiwi,
  market,
  raspberry,
  trading,
  office1,
  office2,
  agrofarm,
  greenenergy,
  financial,
];

const { Header, Content, Footer } = Layout;
const { Title, Paragraph } = Typography;

type Notice = {
  name?: string;
  description?: string;
  attachment?: string;
  link?: string;
  showonpopup?: boolean;
};

type Employee = {
  id?: string | number;
  name?: string;
  first_name?: string;
  last_name?: string;
  fullname?: string;
  full_name?: string;
  title?: string;
  designation?: string;
  position?: string;
  image?: string;
  photo?: string;
  photo_url?: string;
  src?: string;
  attachment?: string;
  bio?: string;
  testimonials?: string;
  testimonial?: string;
  message?: string;
  description?: string;
  email?: string;
  show_testimonial_on_home_screen?: boolean;
};

const Home = () => {
  const navigate = useNavigate();
  const [isVisible, setIsVisible] = useState(false);
  const [showPopUp, setShowPopUp] = useState(false);
  const [popupContent, setPopupContent] = useState<Notice | null>(null);
  const [employees, setEmployees] = useState<Employee[]>([]);

  const targetRef = useRef(null);
  const POPUP_KEY = "popupSeenExpiry";

  function shouldShowPopup(ttl: number) {
    const stored = localStorage.getItem(POPUP_KEY);
    if (!stored) return true;

    const { expiry } = JSON.parse(stored);
    return Date.now() > expiry;
  }

  function markPopupSeen(ttl: number) {
    localStorage.setItem(
      POPUP_KEY,
      JSON.stringify({ expiry: Date.now() + ttl })
    );
  }

  useEffect(() => {
    const observer = new IntersectionObserver((entries) => {
      const entry = entries[0];
      setIsVisible(entry.isIntersecting);
    });

    if (targetRef.current) {
      observer.observe(targetRef.current);
    }

    getNotice();
    getEmployees();

    return () => {
      if (targetRef.current) {
        observer.unobserve(targetRef.current);
      }
    };
  }, []);

  const getNotice = async () => {
    try {
      const response = await axios.post(
        config.prodURL + "notices",
        {
          headers: {
            "Content-Type": "application/json",
          },
        }
      );

      const allNotices = JSON.parse(response.data);
      const popUpContent = allNotices.filter((x: { showonpopup: boolean }) => x.showonpopup === true);
      if (popUpContent.length === 0) {
        setPopupContent(null);
        setShowPopUp(false);
      } else {
        const notice = popUpContent[0];
        setPopupContent(notice);

        if (shouldShowPopup(60 * 60 * 1000)) {
          setShowPopUp(true);
          markPopupSeen(60 * 60 * 1000);
        }
      }
    } catch (error) {
      console.error("Error:", error);
    }
  };

  const getEmployees = async () => {
    try {
      const response = await axios.get(config.prodURL + "getemployees");
      const payload = typeof response.data === "string" ? JSON.parse(response.data) : response.data;
      const allEmployees = Array.isArray(payload) ? payload : payload?.results || payload?.data || [];
      const homeTestimonials = allEmployees.filter(
        (employee: Employee) => employee.show_testimonial_on_home_screen === true
      );
      setEmployees(homeTestimonials);
    } catch (error) {
      console.error("Error fetching employees:", error);
      setEmployees([]);
    }
  };

  const getEmployeeName = (employee: Employee) =>
    `${employee.first_name || ""} ${employee.last_name || ""}`.trim() ||
    employee.name ||
    employee.fullname ||
    employee.full_name ||
    "Team Member";

  const getEmployeeTitle = (employee: Employee) =>
    employee.title || employee.designation || employee.position || "Leadership Team";

  const getEmployeeImage = (employee: Employee) =>
    employee.image || employee.photo || employee.photo_url || employee.src || employee.attachment || malePlaceholder;

  const getEmployeeEmail = (employee: Employee) => {
    const name = getEmployeeName(employee).trim().toLowerCase();
    const matchedProfile = managementTeam.find((profile) =>
      profile.id?.toString() === employee.id?.toString() ||
      profile.name?.trim().toLowerCase() === name
    );

    return employee.email?.trim() || matchedProfile?.email?.trim() || "info@brahmajyotiholdings.com";
  };

  const getEmployeeTestimonialText = (employee: Employee) => {
    const preview = (
      employee.testimonials ||
      employee.testimonial ||
      employee.message ||
      employee.bio ||
      employee.description ||
      ""
    ).replace(/\s+/g, " ").trim();

    return preview || "No testimonial available yet.";
  };

  return (
    <div className='homepage'>
      {showPopUp && popupContent && (
        <div className="overlay" role="dialog" aria-modal="true" aria-labelledby="home-notice-title">
          <div className="popup">
            <button
              className="close-btn"
              onClick={() => setShowPopUp(false)}
              aria-label="Close notice popup"
            >
              &times;
            </button>

            <div className="popup-badge">Important Notice</div>
            <div className="popup-copy">
              <h1 id="home-notice-title" className="popup-title">{popupContent.name || "Latest notice"}</h1>
              <p className="popup-description">
                {popupContent.description || "Please review this notice for the latest company update."}
              </p>
            </div>

            <a
              href={popupContent.link || popupContent.attachment}
              target="_blank"
              rel="noreferrer"
              className="popup-link"
            >
              <div className="popup-image-frame">
                <img className='notice-image' src={popupContent.attachment} alt={popupContent.name || "Popup notice"} />
              </div>
              <span className="popup-cta">Open notice</span>
            </a>
          </div>
        </div>
      )}

      <div className="hero-section">
        <video autoPlay loop muted playsInline className="background-video" >
          <source src={swayambhunath} type="video/mp4" />
          Your browser does not support the video tag.
        </video>
        <Row>
          <Col className="translucent-box">
            <img src={companyname} alt="Slide 4" className="homeboximage" />
            <div className='information'>
              <p className="info-text">
                Incorporated as a Public Limited Company under the Companies Act, 2063. The primary purpose of our company is to own and manage a portfolio of assets and investments, including shares in other companies, real estate, and intellectual property of our client's hard-earned money with the divine intention of multiplying them.
              </p>
              <div>
                <a href="/#/overview"><button className="learn-more-btn">Learn More</button></a>
                <a href="/#/contact"><button className="learn-more-btn">Contact Us</button></a>
              </div>
            </div>
          </Col>
        </Row>
      </div>

      <div className="banner-container" aria-label="Home gallery">
        <div className="banner">
          {homeImages.map((img, i) => (
            <div key={i} className="home-image-frame">
              <img src={img} alt={"Slide " + i} className="home-image" />
            </div>
          ))}
          {homeImages.map((img, i) => (
            <div key={`duplicate-${i}`} className="home-image-frame">
              <img src={img} alt={"Slide duplicate " + i} className="home-image" />
            </div>
          ))}
        </div>
      </div>

      <div className="testimonial-section">
        <div className="testimonial-section-heading">
          <h1>Testimonials</h1>
        </div>

        <Row gutter={[24, 24]} justify="center" className="testimonial-grid">
          {employees.map((employee, index) => (
            <Col
              xs={24}
              sm={12}
              md={8}
              key={employee.id || getEmployeeName(employee) + index}
              className="testimonial-grid-item"
            >
              <div className="testimonial-card-link">
                <Card
                  hoverable
                  className="testimonial-card"
                  onClick={() => navigate(`/testimonials?from=${employee.id}`)}
                  onKeyDown={(event) => {
                    if (event.key === "Enter" || event.key === " ") {
                      event.preventDefault();
                      navigate(`/testimonials?from=${employee.id}`);
                    }
                  }}
                  role="link"
                  tabIndex={0}
                >
                  <div className="testimonial-card-shell">
                    <div className="testimonial-card-media">
                      <img
                        src={getEmployeeImage(employee)}
                        alt={getEmployeeName(employee)}
                        className="testimonial-card-photo"
                      />
                     
                    </div>

                    <div className="testimonial-card-content">
                      <div className="testimonial-card-meta">
                        <Title level={3}>{getEmployeeName(employee)}</Title>
                        <p className="testimonial-card-title">{getEmployeeTitle(employee)}</p>
                        <a
                          href={`mailto:${getEmployeeEmail(employee)}`}
                          className="testimonial-card-email"
                          onClick={(event) => event.stopPropagation()}
                        >
                          {getEmployeeEmail(employee)}
                        </a>
                      </div>

                      <div className="testimonial-card-divider" />

                      <p className="testimonial-copy">
                        {getEmployeeTestimonialText(employee)}
                      </p>
                    </div>
                  </div>
                </Card>
              </div>
            </Col>
          ))}
        </Row>
      </div>

      {/* <div className='team-members'>
        <ManagementTeam />
      </div> */}

      {/* <div className="home-contact">

        Ready to secure your financial future?
        <a href="#/individual">
          <button className='join-btn'>
            Join Now
          </button>
        </a>
      </div> */}

    </div>
  );
};

export default Home;
